import { ref } from 'vue';
import { useRouter } from 'vue-router';
import type { PublicUser } from '@talkvibe/shared';

import { useChatStore } from '../stores/chat.ts';

/**
 * "Message" behaves the same everywhere it appears - profile, user card,
 * friend list - so the open-or-create-then-navigate step lives here rather
 * than being repeated in each view.
 */
export function useStartConversation() {
  const chat = useChatStore();
  const router = useRouter();

  const busyUserId = ref<string | null>(null);
  const error = ref<string | null>(null);

  async function openWith(user: Pick<PublicUser, 'id'>): Promise<void> {
    if (busyUserId.value) return;
    busyUserId.value = user.id;
    error.value = null;
    try {
      const conversation = await chat.startConversation(user.id);
      // The Messages page, so the list stays beside the new conversation.
      await router.push({ name: 'messages', params: { conversationId: conversation.id } });
    } catch (caught) {
      error.value = caught instanceof Error ? caught.message : 'Could not open the conversation.';
    } finally {
      busyUserId.value = null;
    }
  }

  return { openWith, busyUserId, error };
}
